import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SelectOption } from 'projects/component-library/components/input-select/select-option';
import { ModalComponent } from 'projects/component-library/components/modal/modal.component';
import { InviteBulkUsersResponse, InviteUsersRequest } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { UserWithRolesResponse } from 'projects/shared/services/inspection-companies/models/users/user-with-roles.response';
import { ExpectedError, HttpErrorResponse } from 'projects/shared/services/models/error.response';
import { RoleResponse } from 'projects/shared/services/roles/models/role.response';
import { validateForm } from 'projects/shared/validators/shared-validators';
import { Observable } from 'rxjs';

@Component({
  selector: 'shared-invite-users-modal',
  standalone: false,
  styles: [
    `
      @use '../../../component-library/styles/colors.scss' as colors;

      .card {
        background: #f9f9f9;
        border-radius: 4px;
        padding: 1rem;
        margin-bottom: 1rem;
      }

      .card-title {
        font-size: 20px;
        font-weight: 700;
      }

      .card-subtitle {
        .value {
          font-weight: 700;
        }

        color: colors.$neutral-body-grey;
      }

      .delete-icon {
        margin-left: 3.5rem;
        display: inline-flex;
      }

      .right {
        text-align: right;
      }

      .upload-bulk-header {
        display: flex;
        border: 1px solid colors.$brand-primary;
        color: colors.$brand-primary;
        background-color: colors.$brand-1;
        font-weight: bolder;
        padding: 10px;
      }

      .upload-bulk-header a {
        margin-left: auto;
      }
    `,
  ],
  template: `
    <mac-modal [(open)]="isVisible" #modal (openChange)="resetAllFormControls()">
      <mac-modal-title>
        {{ 'Portal.InspectionCompanies.Users.Invite.Title' | translate }}
      </mac-modal-title>

      <mac-modal-content>
        <app-alert type="error" *ngIf="formErrors" [errorKeys]="formErrors || errors"></app-alert>

        <form id="invite-users-form" nz-form [formGroup]="form" (ngSubmit)="onSubmit()" *ngIf="!isBulkInsert">
          <nz-form-item style="margin-top: 1rem" *ngIf="canBulkInvite">
            <nz-form-control [nzXs]="24">
              <span class="upload-bulk-header"
                >{{ 'Portal.InspectionCompanies.Activate.Form.UploadMultipleUsers' | translate }}
                <a (click)="showBulkInsert()">{{ 'Portal.InspectionCompanies.Activate.Form.BulkInsert' | translate }} ></a>
              </span>
            </nz-form-control>
          </nz-form-item>

          <ng-container *ngIf="userInviteForm">
            <mac-form-input-text
              [label]="'Portal.InspectionCompanies.Activate.Form.User.Email.Label' | translate"
              [placeholder]="'Portal.InspectionCompanies.Activate.Form.User.Email.Placeholder' | translate"
              [formControl]="userInviteForm.controls['email']"
              name="email">
            </mac-form-input-text>

            <app-roles-select-input
              [roles$]="roles$"
              [formControl]="userInviteForm.controls['roleKeys']"
              moreInfoLink="roles"
              (error)="onHttpError($event)" />
          </ng-container>

          <nz-form-item style="margin-top: 1rem">
            <nz-form-control [nzXs]="24">
              <mac-button variant="secondary" icon="add" [showAsBlock]="true" (click)="onAddExtraUser()">
                {{ 'Portal.InspectionCompanies.Users.Invite.InviteMore.Label' | translate }}
              </mac-button>
            </nz-form-control>
          </nz-form-item>

          <ng-container *ngFor="let userForm of users.controls; let i = index">
            <div nz-row class="card" nzAlign="middle">
              <div nz-col nzSpan="20">
                <div class="card-title">{{ userForm.controls['email'].value! }}</div>
                <div class="card-subtitle">
                  <label class="label"> {{ 'Portal.InspectionCompanies.Users.Invite.Roles.Label' | translate }} : </label>
                  <ng-container *ngFor="let role of userForm.controls['roleKeys'].value; let i = last">
                    <label class="value"> {{ getRoleLabelKey(role) | translate }}</label>
                    <label *ngIf="!i">, </label>
                  </ng-container>
                </div>
              </div>
              <div nz-col nzSpan="4" class="right">
                <a class="delete-icon" (click)="removeUserForm(i)">
                  <mac-icon icon="delete" class="icon-relative-top"></mac-icon>
                </a>
              </div>
            </div>
          </ng-container>
        </form>

        <ng-container *ngIf="isBulkInsert">
          <shared-invite-users-bulk-form
            (usersInvited)="onBulkUsersInvited()"
            (cancelling)="onBulkUsersCanceled()"
            [bulkInviteUsersAction]="bulkInviteUsersAction" />
        </ng-container>
      </mac-modal-content>

      <mac-modal-footer *ngIf="!isBulkInsert">
        <mac-button form="invite-users-form" type="submit" [isLoading]="isInvitingUsers" (click)="onAddExtraUser()">
          {{ 'Portal.InspectionCompanies.Users.All.InviteUsersButton.Label' | translate }}
        </mac-button>
      </mac-modal-footer>
    </mac-modal>
  `,
})
export class InviteUsersModalComponent implements OnInit {
  @Input() isVisible? = false;
  @Input() inviteUsersAction: (model: InviteUsersRequest[]) => Observable<UserWithRolesResponse[]>;
  @Input() bulkInviteUsersAction: (file: File) => Observable<InviteBulkUsersResponse>;
  @Input({ required: true }) roles$: Observable<RoleResponse[]>;

  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() userInvited = new EventEmitter<void>();

  @ViewChild('modal') modal: ModalComponent;

  roles: SelectOption[] = [];
  isLoadingRoles = false;
  isInvitingUsers = false;
  isBulkInsert = false;
  usersForm: FormArray<FormControl<InviteUserForm | null>>;
  form: FormGroup<InviteUsersForm>;
  userInviteForm?: FormGroup<InviteUserForm>;
  isAllowedToAddExtraUser: boolean;
  errors: ExpectedError[] = [];

  constructor(private formBuilder: FormBuilder, private nzNotificationService: NzNotificationService, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.initializeForm();
    this.fetchRoles();
  }

  get users(): FormArray<FormGroup<InviteUserForm>> {
    return this.form.controls['users'] as FormArray;
  }

  get formErrors(): ValidationErrors | null {
    return this.form?.errors || this.userInviteForm?.errors || this.usersForm?.errors || null;
  }

  get canBulkInvite(): boolean {
    return !!this.bulkInviteUsersAction;
  }

  onSubmit(): void {
    if (this.users.length <= 0) {
      this.usersForm.setErrors({ AtLeaseOneUser: true });
      return;
    }

    validateForm(this.form);
    if (this.form.invalid) {
      return;
    }

    this.inviteUsers();
  }

  openModal(): void {
    this.modal.openModal();
  }

  closeModal(): void {
    this.modal.closeModal();
    this.resetAllFormControls();
  }

  onAddExtraUser(): void {
    if (this.userInviteForm?.invalid) {
      validateForm(this.userInviteForm);
      return;
    }

    if (this.userInviteForm) {
      this.users.push(this.userInviteForm);
    }
    this.usersForm.setErrors(null);
    const rolesOfPreviousUser = this.users.at(this.users.length - 1)?.get('roleKeys')?.value ?? [];
    this.userInviteForm = this.formBuilder.group<InviteUserForm>({
      email: new FormControl<string | null>(null, [Validators.required, Validators.email, Validators.maxLength(255)]),
      roleKeys: new FormControl<string[] | null>(rolesOfPreviousUser, [Validators.required, Validators.minLength(1)]),
    });
  }

  removeUserForm(index: number): void {
    this.users.removeAt(index);
  }

  getRoleLabelKey(roleKey: string): string {
    return `Domain.Role.${roleKey.toLowerCase()}.Label`;
  }

  resetAllFormControls(): void {
    this.isBulkInsert = false;
    this.form.reset();
    this.form.updateValueAndValidity();
    this.form.controls['users'] = this.createUsersForm();

    this.userInviteForm?.reset();
    this.userInviteForm?.updateValueAndValidity();

    this.users.reset();
    this.users.updateValueAndValidity();
  }

  private initializeForm(): void {
    this.usersForm = this.createUsersForm();
    this.form = this.formBuilder.group<InviteUsersForm>({
      users: this.usersForm,
    });

    this.onAddExtraUser();
  }

  private createUsersForm(): FormArray<FormControl<InviteUserForm | null>> {
    return this.formBuilder.array<InviteUserForm>([]);
  }

  protected showBulkInsert(): void {
    this.isBulkInsert = true;
  }

  private fetchRoles(): void {
    this.isLoadingRoles = true;

    this.roles$.subscribe({
      next: (roles: RoleResponse[]) => {
        this.roles = roles.map((role) => ({
          id: role.key,
          label: `Domain.Role.${role.key.toLowerCase()}.Label`,
        }));
      },
      error: () => {
        this.isLoadingRoles = false;
        this.form.disable();
      },
      complete: () => {
        this.isLoadingRoles = false;
      },
    });
  }

  private inviteUsers(): void {
    this.isInvitingUsers = true;
    this.form.disable();

    const users: InviteUsersRequest[] = this.users.value.map((user) => ({
      email: user.email!,
      roles: user.roleKeys!,
    }));

    this.inviteUsersAction(users).subscribe({
      next: () => {
        this.nzNotificationService.success(
          this.translateService.instant('Portal.InspectionCompanies.Users.All.InviteUser.Success.Title'),
          this.translateService.instant('Portal.InspectionCompanies.Users.All.InviteUser.Success.Message')
        );
        this.userInvited.emit();
      },
      error: (response: HttpErrorResponse) => {
        this.isInvitingUsers = false;
        this.form.enable();
        this.errors = response.error.errors;
      },
      complete: () => {
        this.isInvitingUsers = false;
        this.resetAllFormControls();
        this.form.enable();
      },
    });
  }

  protected onBulkUsersInvited(): void {
    this.isBulkInsert = false;
    this.userInvited.emit();
  }

  protected onBulkUsersCanceled(): void {
    this.isBulkInsert = false;
    this.userInvited.emit();
  }

  onHttpError(error: string) {
    const httpError = error as ExpectedError;
    this.errors = [...this.errors, httpError];
  }
}

interface InviteUsersForm {
  users: FormArray<FormControl<InviteUserForm | null>>;
}

interface InviteUserForm {
  email: FormControl<string | null>;
  roleKeys: FormControl<string[] | null>;
}
